import {
  globalSchema,
  type MedplumClient,
  matchesSearchRequest as medplumMatchesSearchRequest,
  parseSearchRequest as medplumParseSearchRequest,
  type QueryTypes,
  type SearchRequest,
  type TypeInfo,
} from '@medplum/core'
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { invariant } from '#src/invariant.ts'

export function matchesSearchRequest<T extends ResourceType>(
  resource: ExtractResource<T>,
  searchRequest: SearchRequest<ExtractResource<T>>,
  schema: Schema<T>
): boolean {
  invariant(
    !!schema.getTypeInfo(),
    `${schema.type} schema missing. Call medplum.requestSchema first.`
  )
  return medplumMatchesSearchRequest(resource, searchRequest)
}

export function parseSearchRequest<T extends ResourceType>(
  url: string | URL,
  schema: Schema<T>,
  query?: Record<string, string[] | string | undefined>
): SearchRequest<ExtractResource<T>> {
  invariant(
    !!schema.getTypeInfo(),
    `${schema.type} schema missing. Call medplum.requestSchema first.`
  )
  return medplumParseSearchRequest(url, query)
}

export interface Schema<T extends ResourceType> {
  type: T // Don't use `resourceType` since `isResource` depends on it and a `Schema` is not a `Resource`.
  getTypeInfo: () => TypeInfo
}

export function tryGetSchema<T extends ResourceType>(
  resourceType: T
): Schema<T> | null {
  return globalSchema.types[resourceType]
    ? {
        type: resourceType,
        getTypeInfo: () => globalSchema.types[resourceType],
      }
    : null
}

// Medplum's `medplum.fhirSearchUrl` does not sort the search params, so we should use this wherever possible to avoid mistakenly assuming the parameters will be sorted when doing comparisons, keying, etc…
export function fhirSearchUrl(
  resourceType: ResourceType,
  query: QueryTypes,
  medplum: MedplumClient
): URL {
  const url = medplum.fhirSearchUrl(resourceType, query)
  url.searchParams.sort() // Medplum calls this internally in a bunch of their functions, but not in `fhirSearchUrl`. For example: https://github.com/medplum/medplum/blob/f9571356b2477b561e636a20e75a160f00f1ae57/packages/core/src/client.ts#L1530
  return url
}
